/**
 * EditBusiness Page
 * Single-page edit form with all sections visible at once
 * Better UX with collapsible sections and improved styling
 */
import { Loader, AlertCircle, CheckCircle, ChevronDown, Upload, X, MapPin, Image as ImageIcon } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useState, useEffect } from 'react';
import { useEditBusiness } from '../hooks/useEditBusiness.js';
import { LocationResolver } from '../domain/services/LocationResolver.js';
import LocationPickerModal from '../components/form/LocationPickerModal';
import HoursModal from '../components/form/HoursModal';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const EditBusiness = () => {
  const vm = useEditBusiness();
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    location: true,
    media: true,
    hours: true,
    services: false,
  });
  const [locationNames, setLocationNames] = useState({});

  // Resolve location names from IDs
  useEffect(() => {
    if (vm.formData.mapAddress?.province || vm.formData.mapAddress?.district || vm.formData.mapAddress?.municipality) {
      const resolved = LocationResolver.resolveAddress({
        province: vm.formData.mapAddress.province,
        district: vm.formData.mapAddress.district,
        municipality: vm.formData.mapAddress.municipality,
      }, 'english');
      setLocationNames(resolved);
    }
  }, [vm.formData.mapAddress]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (vm.loading && !vm.business) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="flex items-center justify-center py-20">
          <Loader className="w-8 h-8 text-emerald-400 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Sticky Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-emerald-500/20 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => vm.navigate('/dashboard?tab=my-businesses')}
              className="text-slate-400 hover:text-emerald-400 transition"
            >
              ← Back
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-white">{vm.business?.name}</h1>
              <p className="text-xs sm:text-sm text-slate-400">Edit Business Profile</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-4">

        {/* Alerts */}
        {vm.error && (
          <div className="mb-4 bg-red-50/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-300 text-sm">{vm.error}</p>
          </div>
        )}
        {vm.success && (
          <div className="mb-4 bg-green-50/10 border border-green-500/30 rounded-lg p-3 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
            <p className="text-green-300 text-sm">{vm.success}</p>
          </div>
        )}

        <form id="edit-business-form" onSubmit={vm.handleSubmit} className="space-y-4">
          {/* Basic Information Section */}
          <Section
            title="Basic Information"
            section="basic"
            expanded={expandedSections.basic}
            onToggle={toggleSection}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Business Name" required>
                <input
                  type="text"
                  name="grouped"
                  value={vm.formData.grouped}
                  onChange={vm.handleChange}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
                  placeholder="Enter business name"
                />
              </FormField>

              <FormField label="kahaTAG" required>
                <input
                  type="text"
                  name="tag"
                  value={vm.formData.tag}
                  readOnly
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-700 rounded-xl text-slate-400 placeholder-slate-600 cursor-not-allowed opacity-75"
                  placeholder="e.g., CHAILA"
                />
              </FormField>

              <FormField label="Slogan" optional>
                <input
                  type="text"
                  name="tagLine"
                  value={vm.formData.tagLine}
                  onChange={vm.handleChange}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
                  placeholder="Short Slogan"
                />
              </FormField>

              <FormField label="Contact Number" required>
                <input
                  type="tel"
                  name="contactNumber"
                  value={vm.formData.contactNumber}
                  readOnly
                  className="w-full px-4 py-3 bg-slate-700/50 border border-slate-700 rounded-xl text-slate-400 placeholder-slate-600 cursor-not-allowed opacity-75"
                  placeholder="+977 98XXXXXXXX"
                />
              </FormField>

              <FormField label="Landline" optional>
                <input
                  type="tel"
                  name="landline"
                  value={vm.formData.landline || ''}
                  onChange={vm.handleChange}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
                  placeholder="Landline number"
                />
              </FormField>

              <FormField label="Email" optional>
                <input
                  type="email"
                  name="email"
                  value={vm.formData.email}
                  onChange={vm.handleChange}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
                  placeholder="email@example.com"
                />
              </FormField>

              <FormField label="Website" optional>
                <input
                  type="url"
                  name="website"
                  value={vm.formData.website}
                  onChange={vm.handleChange}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
                  placeholder="https://example.com"
                />
              </FormField>

              <FormField label="Building Floor Number" optional>
                <input
                  type="text"
                  name="floorNo"
                  value={vm.formData.floorNo}
                  onChange={vm.handleChange}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
                  placeholder="e.g., 2nd Floor"
                />
              </FormField>
            </div>

            <FormField label="Description" optional>
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-4">
                <textarea
                  name="description"
                  value={vm.formData.description}
                  onChange={vm.handleChange}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition break-words overflow-hidden"
                  placeholder="Describe your business..."
                  style={{ minHeight: '100px', resize: 'vertical', wordWrap: 'break-word', overflowWrap: 'break-word' }}
                />
              </div>
            </FormField>

            <FormField label="Building Information" optional>
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-4">
                <textarea
                  name="buildingInformation"
                  value={vm.formData.buildingInformation}
                  onChange={vm.handleChange}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
                  placeholder="Additional building details..."
                  style={{ minHeight: '80px', resize: 'vertical' }}
                />
              </div>
            </FormField>
          </Section>

          {/* Location Section */}
          <Section
            title="Location"
            section="location"
            expanded={expandedSections.location}
            onToggle={toggleSection}
          >
            <button
              type="button"
              onClick={() => vm.setShowLocationPicker(true)}
              className="w-full mb-4 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold transition flex items-center justify-center gap-2"
            >
              <MapPin className="w-5 h-5" />
              Pick Location on Map
            </button>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FormField label="Province">
                <input
                  type="text"
                  value={locationNames.province || ''}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
                  placeholder="Province"
                  readOnly
                />
              </FormField>

              <FormField label="District">
                <input
                  type="text"
                  value={locationNames.district || ''}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
                  placeholder="District"
                  readOnly
                />
              </FormField>

              <FormField label="Municipality">
                <input
                  type="text"
                  value={locationNames.municipality || ''}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
                  placeholder="Municipality"
                  readOnly
                />
              </FormField>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Ward Number">
                <input
                  type="text"
                  name="wardNo"
                  value={vm.formData.mapAddress.wardNo}
                  onChange={(e) => vm.handleNestedObjectChange('mapAddress', 'wardNo', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
                  placeholder="Ward No."
                />
              </FormField>

              <FormField label="Street">
                <input
                  type="text"
                  name="street"
                  value={vm.formData.mapAddress.street}
                  onChange={(e) => vm.handleNestedObjectChange('mapAddress', 'street', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
                  placeholder="Street name"
                />
              </FormField>
            </div>

            <FormField label="General Address">
              <input
                type="text"
                name="generalAddress"
                value={vm.formData.mapAddress.generalAddress}
                onChange={(e) => vm.handleNestedObjectChange('mapAddress', 'generalAddress', e.target.value)}
                className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
                placeholder="General address"
              />
            </FormField>

            <FormField label="Additional Information">
              <textarea
                name="additionalInfo"
                value={vm.formData.mapAddress.additionalInfo}
                onChange={(e) => vm.handleNestedObjectChange('mapAddress', 'additionalInfo', e.target.value)}
                rows="2"
                className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition resize-none"
                placeholder="Additional location details..."
              />
            </FormField>
          </Section>

          {/* Media Section */}
          <Section
            title="Media"
            section="media"
            expanded={expandedSections.media}
            onToggle={toggleSection}
          >
            <div className="space-y-6">
              <div>
                <h3 className="text-slate-300 font-semibold mb-3 text-sm">Business Images</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <ImageUploadField
                    label="Avatar"
                    value={vm.formData.avatar}
                    onChange={(e) => vm.handleImageUpload(e, 'avatar')}
                    inputRef={vm.avatarInputRef}
                    loading={vm.uploadingImage}
                  />

                  <ImageUploadField
                    label="Cover Image"
                    value={vm.formData.coverImageUrl}
                    onChange={(e) => vm.handleImageUpload(e, 'coverImageUrl')}
                    inputRef={vm.coverInputRef}
                    loading={vm.uploadingImage}
                  />

                  <ImageUploadField
                    label="Building Image"
                    value={vm.formData.buildingImageUrl}
                    onChange={(e) => vm.handleImageUpload(e, 'buildingImageUrl')}
                    inputRef={vm.buildingInputRef}
                    loading={vm.uploadingImage}
                  />
                </div>
              </div>

              <div className="border-t border-slate-700/50 pt-6">
                <h3 className="text-slate-300 font-semibold mb-3 text-sm">Gallery</h3>
                <div className="mb-4">
                  <label className="block text-slate-300 font-semibold mb-2 text-sm">
                    Upload Gallery Images ({vm.formData.gallery.length}/5)
                  </label>
                  <button
                    type="button"
                    onClick={() => vm.galleryInputRef.current?.click()}
                    disabled={vm.uploadingImage || vm.formData.gallery.length >= 5}
                    className="w-full px-4 py-8 border-2 border-dashed border-slate-600 rounded-xl text-slate-400 hover:border-emerald-500 hover:text-emerald-400 transition disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center gap-2"
                  >
                    <ImageIcon className="w-8 h-8" />
                    <span className="text-sm font-medium">Click to upload or drag and drop</span>
                    <span className="text-xs">PNG, JPG up to 5MB</span>
                  </button>
                  <input
                    type="file"
                    ref={vm.galleryInputRef}
                    onChange={vm.handleGalleryUpload}
                    multiple
                    accept="image/*"
                    className="hidden"
                  />
                </div>

                {vm.formData.gallery.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    {vm.formData.gallery.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image}
                          alt={`Gallery ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => vm.handleRemoveGalleryImage(index)}
                          className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white p-1 rounded-lg opacity-0 group-hover:opacity-100 transition"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Section>

          {/* Hours Section */}
          <Section
            title="Working Hours"
            section="hours"
            expanded={expandedSections.hours}
            onToggle={toggleSection}
          >
            <button
              type="button"
              onClick={() => vm.setShowHoursModal(true)}
              className="w-full px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold transition"
            >
              Edit Working Hours
            </button>

            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => {
                const hours = vm.workingDaysAndHours[day];
                const timeStr = hours?.open && hours?.close ? `${vm.formatTime(hours.open)} - ${vm.formatTime(hours.close)}` : 'Not set';
                return (
                  <div key={day} className="bg-slate-800/30 border border-slate-700 rounded-lg p-3">
                    <p className="text-slate-300 font-medium capitalize mb-1 text-xs">{day.slice(0, 3)}</p>
                    <p className="text-slate-400 text-xs">{timeStr}</p>
                  </div>
                );
              })}
            </div>
          </Section>

          {/* Footer Actions */}
          <div className="flex gap-3 pt-6 border-t border-slate-700">
            <button
              type="button"
              onClick={() => vm.navigate('/dashboard?tab=my-businesses')}
              className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-semibold transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={vm.loading}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-emerald-500/50 transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {vm.loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
        </div>
      </div>

      {/* Modals */}
      {vm.showLocationPicker && (
        <LocationPickerModal
          formData={vm.formData}
          mapCenter={vm.mapCenter}
          searchQuery={vm.searchQuery}
          onSearchChange={vm.handleSearchLocation}
          onMapClick={(lat, lng) => vm.getLocationFromCoordinates(lat, lng)}
          onCoordinateChange={(key, val) => {
            console.log(`Coordinate change: ${key} = ${val}`);
            vm.setFormData(prev => ({ ...prev, [key]: val }));
          }}
          onAddressChange={(key, val) => {
            console.log(`Address change: ${key} = ${val}`);
            vm.setFormData(prev => ({ ...prev, mapAddress: { ...prev.mapAddress, [key]: val } }));
          }}
          getLocationFromCoordinates={vm.getLocationFromCoordinates}
          onClose={() => {
            console.log('Location picker closing. Current generalAddress:', vm.formData.mapAddress?.generalAddress);
            vm.setShowLocationPicker(false);
          }}
        />
      )}
      {vm.showHoursModal && (
        <HoursModal
          workingDaysAndHours={vm.workingDaysAndHours}
          onHoursChange={vm.handleHoursChange}
          onApplyAll={vm.applyHoursToAllDays}
          onClose={() => vm.setShowHoursModal(false)}
        />
      )}
    </div>
  );
};

// Helper Components
const Section = ({ title, section, expanded, onToggle, children }) => (
  <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl overflow-hidden shadow-lg hover:shadow-emerald-500/10 transition">
    <button
      type="button"
      onClick={() => onToggle(section)}
      className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-800/60 transition"
    >
      <div className="flex items-center gap-3">
        <div className="w-1 h-6 bg-gradient-to-b from-emerald-500 to-emerald-600 rounded-full" />
        <h2 className="text-lg font-semibold text-white">{title}</h2>
      </div>
      <ChevronDown
        className={`w-5 h-5 text-slate-400 transition transform ${expanded ? 'rotate-180' : ''}`}
      />
    </button>
    {expanded && (
      <div className="px-6 py-4 border-t border-slate-700/50 space-y-4">
        {children}
      </div>
    )}
  </div>
);

const FormField = ({ label, required, children }) => (
  <div>
    <label className="block text-slate-300 font-medium mb-2 text-sm">
      {label}
      {required && <span className="text-red-400 ml-1">*</span>}
    </label>
    {children}
  </div>
);

const ImageUploadField = ({ label, value, onChange, inputRef, loading }) => (
  <div>
    <label className="block text-slate-300 font-medium mb-2 text-sm">{label}</label>
    <input
      type="file"
      ref={inputRef}
      onChange={onChange}
      accept="image/*"
      className="hidden"
    />
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      disabled={loading}
      className="w-full aspect-square bg-slate-800/50 border-2 border-dashed border-slate-600 rounded-xl hover:border-emerald-500 hover:bg-slate-800/70 transition disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center gap-2 group"
    >
      {value ? (
        <img src={value} alt={label} className="w-full h-full object-cover rounded-lg" />
      ) : (
        <>
          <Upload className="w-6 h-6 text-slate-500 group-hover:text-emerald-400 transition" />
          <span className="text-xs text-slate-400 group-hover:text-emerald-400 transition">{label}</span>
        </>
      )}
    </button>
  </div>
);

export default EditBusiness;
